import { FC, ComponentType } from "react";
import { Route, RouteProps, Redirect, RouteComponentProps } from "react-router-dom";
import { useGrowthBook } from "@growthbook/growthbook-react";

import { useFeatureFlag, FeatureFlag } from "../../hooks/useFeatureFlag";

interface FeatureFlagRouteProps extends RouteProps {
  flag: FeatureFlag;
  component: ComponentType<any>;
  redirectTo?: string | ((props: RouteComponentProps<any>) => string);
}

/**
 * Route wrapper that checks if a feature flag is enabled
 * Redirects to a fallback route if the feature is disabled
 * Waits for GrowthBook to be ready to avoid false redirects on page refresh
 *
 * @example
 * <FeatureFlagRoute
 *   flag={FEATURE_FLAGS.COMPLIANCE}
 *   path="/crew/:crewId/members-v2"
 *   component={MembersList}
 *   redirectTo="/crew/:crewId/members"
 * />
 */
export const FeatureFlagRoute: FC<FeatureFlagRouteProps> = ({
  flag,
  component: Component,
  redirectTo,
  ...routeProps
}) => {
  const growthbook = useGrowthBook();
  const isEnabled = useFeatureFlag(flag);

  return (
    <Route
      {...routeProps}
      render={(props) => {
        // Wait for GrowthBook ready + attributes before checking feature flag
        const attrs = growthbook?.getAttributes();
        if (!growthbook?.ready || !(attrs?.crewId || attrs?.userId)) {
          return null;
        }

        if (!isEnabled) {
          const path = typeof redirectTo === "function" ? redirectTo(props) : redirectTo || "/";
          return <Redirect to={path} />;
        }

        return <Component {...props} />;
      }}
    />
  );
};

export default FeatureFlagRoute;
